import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Product Rewards Custom Element
 * 
 * @description A custom element that displays product rewards points and automatically
 * updates when product variants change. This component listens for variant update events
 * from the variant picker and updates the rewards display with the new variant's price-based points.
 * 
 * @class ProductRewards
 * @extends HTMLElement
 * 
 * @example
 * <product-rewards data-product-id="123456">
 *   <div class="product-rewards">
 *     <p>You'll earn <span class="product-rewards__points">13 points</span> for this purchase</p>
 *   </div>
 * </product-rewards>
 * 
 * @dependencies
 * - @theme/events - ThemeEvents and VariantUpdateEvent types
 * - snippets/product-rewards.liquid - Liquid template that renders the rewards HTML
 * 
 * @event-listens ThemeEvents.variantUpdate
 * Fired when a variant is selected, contains updated HTML in event.detail.data.html
 */
class ProductRewards extends HTMLElement {
  /**
   * Called when the element is inserted into the DOM.
   * Sets up event listener for variant updates.
   * 
   * @returns {void}
   */
  connectedCallback() {
    // Find the closest section or dialog to attach event listener
    // This ensures we only listen to events within the same product context
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    
    // Listen for variant update events from the variant picker
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateRewards);
  }

  /**
   * Called when the element is removed from the DOM.
   * Cleans up event listener to prevent memory leaks.
   * 
   * @returns {void}
   */
  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    
    // Remove event listener to prevent memory leaks
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updateRewards);
  }

  /**
   * Updates the rewards section with new variant data.
   * 
   * This method is called when a variant update event is fired. It extracts the
   * updated rewards HTML from the event and replaces the current rewards display
   * if the content has changed.
   * 
   * @param {VariantUpdateEvent} event - The variant update event containing new HTML
   * @property {Object} event.detail.data - Event data object
   * @property {Object} event.detail.data.newProduct - New product object (if product changed)
   * @property {DocumentFragment} event.detail.data.html - Updated HTML fragment
   * 
   * @returns {void}
   */
  updateRewards = (event) => {
    // If this is a new product, update the product ID
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } 
    // If event is for a different product, ignore it
    else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Find the new rewards HTML in the updated fragment
    const newRewards = event.detail.data.html.querySelector('.product-rewards');
    // Find the current rewards element in this component
    const currentRewards = this.querySelector('.product-rewards');

    // If either element is missing, nothing to update
    if (!newRewards || !currentRewards) return;

    // Only update if content has actually changed (performance optimization)
    if (currentRewards.innerHTML !== newRewards.innerHTML) {
      // Replace current element with new one (preserves event listeners on parent)
      currentRewards.replaceWith(newRewards.cloneNode(true));
    }
  };
}

if (!customElements.get('product-rewards')) {
  customElements.define('product-rewards', ProductRewards);
}
